/**
 * Team Design — team-name overlay (auto-load layer).
 *
 * When the shopper has typed a team name, every visible card should render
 * its colored pattern SVG with the name overlaid — without the shopper
 * having to hover. This file owns that "load on its own" behavior:
 *
 *  - applyAutoPreview      — write the colored SVG into the preview slot
 *  - clearAutoLoad         — undo an auto-load (keeps the SVG intact while a
 *                            logo is still active so the logo overlay survives
 *                            text being cleared)
 *  - renderAutoPreview     — fetch + colorize + apply for one card, guarded
 *                            against hover ownership and thumbnail strip
 *  - autoRenderVisibleCards — fan out renderAutoPreview to every card the
 *                            IntersectionObserver currently has in view
 *  - attachThumbnailHandlers — hide auto-load during thumb-strip hover, then
 *                            re-apply on mouseleave
 *
 * Depends on:
 *   window.TeamDesignSvg                        (fetch + colorize)
 *   window.CollectionTeamText.{state, dom}      (render layer)
 *
 * Exposes window.CollectionTeamText.auto.
 */
(function () {
  'use strict';

  var NS    = (window.CollectionTeamText = window.CollectionTeamText || {});
  var state = NS.state;
  var dom   = NS.dom;
  var svg   = window.TeamDesignSvg;

  if (!state || !dom || !svg) return; // render layer or svg engine didn't load

  function applyAutoPreview(previewEl, mediaWrap, coloredSvg) {
    if (mediaWrap) mediaWrap.style.visibility = 'hidden';
    previewEl.dataset.autoLoaded = '1';
    previewEl.innerHTML = coloredSvg;
    previewEl.style.display = 'block';
  }

  function clearAutoLoad(card) {
    var previewEl = dom.getPreview(card);
    if (!previewEl || previewEl.dataset.autoLoaded !== '1') return;

    // While a swatch is hovered, the hover system owns the preview's display
    // state — don't yank it out from under the user. Just drop our flags so
    // the next mouseleave restores the default product image.
    if (card.querySelector('.card-color-swatch--active')) {
      delete previewEl.dataset.autoLoaded;
      previewEl._autoColoredSvg = null;
      return;
    }

    // Keep the SVG visible when ANY other customization intent is still
    // active:
    //   - logo still uploaded
    //   - banner color theme picked       (state.bannerTheme)
    //   - banner font pair picked         (state.bannerTextFont || state.bannerNumberFont)
    // inject.refreshAllVisiblePreviews() (called right after clearAll) will
    // re-run injectTeamText, which renders the "TEAM NAME" placeholder
    // when state.currentText is empty so the colorized jersey + active
    // picker selections stay visible.
    //
    // dataset.autoLoaded is preserved on purpose — the SVG is still under
    // auto-system management, just by a different driver (banner instead
    // of team text). The thumbnail-hover handlers don't actually look at
    // this flag anymore (they key off DOM state), but downstream consumers
    // that DO check it should still see the "is auto-managed" signal.
    if (state.currentLogoUrl || state.bannerTheme || state.bannerTextFont || state.bannerNumberFont) {
      previewEl._autoColoredSvg = null;
      return;
    }

    var mediaWrap = dom.getMediaWrap(card);
    delete previewEl.dataset.autoLoaded;
    previewEl._autoColoredSvg = null;
    previewEl.style.display = 'none';
    previewEl.innerHTML = '';
    if (mediaWrap) mediaWrap.style.visibility = '';
  }

  function clearAllAutoLoaded() {
    var cards = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) clearAutoLoad(cards[i]);
  }

  function renderAutoPreview(card) {
    // Gate on `state.armed` — without this, cookie-restored team text /
    // logo would auto-paint every visible card on page load, even though
    // the shopper hasn't interacted on THIS visit. arm() is flipped by
    // the four interaction sites (team-input focus, logo-upload focus /
    // click, color swatch click, font click) and only then do the
    // intent-driven render paths below get to run.
    if (!state.armed) return;

    // Render when ANY banner-driven intent is set:
    //   - team text typed             (state.autoMode + state.currentText)
    //   - banner font pair picked     (state.bannerTextFont || state.bannerNumberFont)
    //   - banner color picked         (state.bannerTheme)
    // The last two cases render with the "TEAM NAME" placeholder (see
    // text-render.js injectTeamText) so the picker has visible output
    // even before the shopper has typed anything.
    var hasIntent = (state.autoMode && state.currentText) ||
                    state.bannerTextFont ||
                    state.bannerNumberFont ||
                    state.bannerTheme;
    if (!hasIntent) return;
    // Hover owns the preview while a swatch is active — we'll auto-load when
    // the user leaves (the grid MutationObserver re-apply branch handles it).
    if (card.querySelector('.card-color-swatch--active')) return;
    // Thumbnail strip is being hovered — let the underlying <img> swap show
    // through; we'll re-apply on thumb mouseleave.
    if (card._thumbHover) return;

    var previewEl = dom.getPreview(card);
    if (!previewEl) return;

    // Already auto-loaded and the SVG is still in the DOM — nothing to do.
    if (previewEl.dataset.autoLoaded === '1' && previewEl.firstChild) return;

    var swatch = dom.getFirstSwatch(card);
    if (!swatch) return; // swatches not yet populated — observer will retry
    var thumbnail = swatch.dataset.thumbnail || '';
    if (!thumbnail) return;

    // Sync re-apply from cache (used after a hover-leave or thumb-leave).
    if (previewEl._autoColoredSvg) {
      applyAutoPreview(previewEl, dom.getMediaWrap(card), previewEl._autoColoredSvg);
      return;
    }

    svg.fetch(thumbnail).then(function (svgText) {
      if (!svgText) return;
      // Re-check after async — input may have cleared, card scrolled away,
      // swatch hovered, or thumbnail being touched. Same intent test as
      // the entry guard at the top of renderAutoPreview.
      var stillHasIntent = (state.autoMode && state.currentText) ||
                           state.bannerTextFont ||
                           state.bannerNumberFont ||
                           state.bannerTheme;
      if (!stillHasIntent) return;
      if (!state.visibleCards.has(card)) return;
      if (card.querySelector('.card-color-swatch--active')) return;
      if (card._thumbHover) return;

      // Banner picker wins when set — same source of truth as the team-text
      // overlay (see team-design.text-render.js readActiveTheme). Without
      // this, the jersey would render with a per-card random color (often
      // red, from the first swatch in the sampled list) while the team text
      // recoloured to the banner theme, producing a jarring mismatch.
      var banner = state.bannerTheme;
      var theme  = banner
        ? {
            primary:   banner.primaryColor   || '',
            secondary: banner.secondaryColor || '',
            tertiary:  banner.tertiaryColor  || '',
            accent:    banner.accentColor    || '',
          }
        : {
            primary:   swatch.dataset.primary   || '',
            secondary: swatch.dataset.secondary || '',
            tertiary:  swatch.dataset.tertiary  || '',
            accent:    swatch.dataset.accent    || '',
          };
      var colored = svg.colorize(svgText, theme);
      previewEl._autoColoredSvg = colored;
      applyAutoPreview(previewEl, dom.getMediaWrap(card), colored);
    });
  }

  function autoRenderVisibleCards() {
    state.visibleCards.forEach(renderAutoPreview);
  }

  // While the cursor is over the thumbnail strip, card-product.liquid swaps
  // the underlying <img> src to show the hovered variant. The SVG overlay
  // sits on top of that <img>, so we hide the overlay during thumb-hover
  // and re-apply it on leave. Swatch hover is unaffected — those handlers
  // live elsewhere.
  //
  // We key off DOM state (is there a visible SVG?) rather than the
  // `dataset.autoLoaded` flag — that flag is cleared by clearAutoLoad
  // when team text is removed even though banner state keeps the SVG
  // visible, and the hover logic should not care which loader put it
  // there in the first place.
  function attachThumbnailHandlers(card) {
    var thumbOuter = card.querySelector('.card-product-figma__thumb-outer');
    if (!thumbOuter || thumbOuter._teamTextHandled) return;
    thumbOuter._teamTextHandled = true;

    thumbOuter.addEventListener('mouseenter', function () {
      card._thumbHover = true;
      var previewEl = dom.getPreview(card);
      // Nothing to hide if no overlay is currently rendered.
      if (!previewEl || !previewEl.firstChild) return;
      if (previewEl.style.display === 'none') return;
      var mediaWrap = dom.getMediaWrap(card);
      previewEl.style.display = 'none';
      if (mediaWrap) mediaWrap.style.visibility = '';
    });

    thumbOuter.addEventListener('mouseleave', function () {
      card._thumbHover = false;
      // Restore the overlay if ANY customization intent is still active.
      // Banner color/font alone should still bring the overlay back when
      // the cursor leaves the thumb strip.
      var shouldRestore = (state.autoMode && state.currentText) ||
                          state.currentLogoUrl ||
                          state.bannerTheme ||
                          state.bannerTextFont ||
                          state.bannerNumberFont;
      if (!shouldRestore) return;
      var previewEl = dom.getPreview(card);
      if (!previewEl) return;

      // The SVG already in the DOM is the source of truth — its colors
      // reflect the most recent banner-theme update, while `_autoColoredSvg`
      // may be stale (it's only refreshed by renderAutoPreview; the banner
      // path applyThemeToCard in banner-swatches.js writes innerHTML
      // directly without updating the cache). Mouseenter only flipped
      // display:none, so the colorized SVG plus its overlays are still
      // there — just restore visibility.
      if (previewEl.firstChild) {
        var mediaWrap = dom.getMediaWrap(card);
        if (mediaWrap) mediaWrap.style.visibility = 'hidden';
        previewEl.style.display = 'block';
        return;
      }
      // SVG was removed from the DOM (e.g. by a fallback clear) but we
      // still have a cached copy — re-inject as a recovery path.
      if (previewEl._autoColoredSvg) {
        applyAutoPreview(previewEl, dom.getMediaWrap(card), previewEl._autoColoredSvg);
        return;
      }
      // No SVG in the DOM at all — fall back to a full reload.
      renderAutoPreview(card);
    });
  }

  // ─── Public API ───────────────────────────────────────────────────────────
  NS.auto = {
    apply:               applyAutoPreview,
    clear:               clearAutoLoad,
    clearAll:            clearAllAutoLoaded,
    render:              renderAutoPreview,
    renderVisible:       autoRenderVisibleCards,
    attachThumbHandlers: attachThumbnailHandlers,
  };
})();
